import { useEffect, useState } from "react";
import {
  Title,
  TextInput,
  Button,
  Container,
  Stack,
  Group,
  Text,
  ActionIcon,
  Loader,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useLocation, useParams } from "wouter";
import { getMcpServer, updateMcpServer } from "./mcpServerApi";
import { notifications } from "@mantine/notifications";

function headersDictToList(dict) {
  if (!dict) return [];
  return Object.entries(dict).map(([key, value]) => ({ key, value }));
}

function headersListToDict(list) {
  if (!list?.length) return undefined;
  const dict = {};
  for (const { key, value } of list) {
    if (key) dict[key] = value;
  }
  return Object.keys(dict).length > 0 ? dict : undefined;
}

export default function McpServerEdit() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const [loading, setLoading] = useState(true);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      url: "",
      headers: [],
    },
    validate: {
      name: (v) => (v ? null : "Name is required"),
      url: (v) =>
        v && (v.startsWith("http://") || v.startsWith("https://"))
          ? null
          : "Valid URL required",
    },
  });

  useEffect(() => {
    getMcpServer(params.serverId).then((server) => {
      form.setValues({
        name: server.name,
        url: server.url,
        headers: headersDictToList(server.headers),
      });
      setLoading(false);
    });
    // Mantine form object has unstable identity; including it would rerun this effect every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.serverId]);

  const handleSubmit = async (values) => {
    try {
      await updateMcpServer(params.serverId, {
        ...values,
        headers: headersListToDict(values.headers),
      });
      setLocation("/mcp-servers");
    } catch (err) {
      notifications.show({
        title: "Error",
        message: err.message,
        color: "red",
      });
    }
  };

  if (loading) return <Loader />;

  return (
    <Container size="sm">
      <Title order={1} mb="md">
        Edit MCP Server
      </Title>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            label="Name"
            placeholder="My MCP Server"
            {...form.getInputProps("name")}
            radius="md"
          />
          <TextInput
            label="URL"
            placeholder="https://mcp.example.com/api/mcp"
            {...form.getInputProps("url")}
            radius="md"
          />

          <Group mt="md" justify="space-between" align="center">
            <Text fw={500}>Authentication Headers</Text>
            <Button
              variant="outline"
              size="compact-sm"
              onClick={() =>
                form.insertListItem("headers", { key: "", value: "" })
              }
            >
              Add Header
            </Button>
          </Group>
          {form.values.headers?.map((_, idx) => (
            <Group key={idx} mb="xs" align="flex-end">
              <TextInput
                label="Header"
                placeholder="Authorization"
                {...form.getInputProps(`headers.${idx}.key`)}
                radius="md"
                style={{ flex: 1 }}
              />
              <TextInput
                label="Value"
                placeholder="Bearer your-token-here"
                {...form.getInputProps(`headers.${idx}.value`)}
                radius="md"
                style={{ flex: 2 }}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                mb={4}
                onClick={() => form.removeListItem("headers", idx)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}

          <Button mt="lg" type="submit">
            Save Changes
          </Button>
        </Stack>
      </form>
    </Container>
  );
}
